/** @jsx jsx */
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	type PointerEvent,
	type FocusEvent,
} from 'react';
import { css, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
// TODO HL-1430 move this constant into our package once JWM business page is replaced with ours
import { BOARD_ISSUES_JQL } from '@atlassian/jira-business-summary-services/src/services/constants.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { Link, useRouterActions } from '@atlassian/react-resource-router';
import {
	DonutChart as Donut,
	useActiveSeriesKey,
	useActiveSeriesKeyEmitter,
} from '@atlassian/viz-platform-charts--next/charts';
import { element, useElementProps } from '@atlassian/viz-platform-charts--next/core';

import { useSectionAnalytics, type LinkTarget } from '../../../common/ui/analytics/index.tsx';
import { ChartWithHeaderContainer } from '../../../common/ui/chart-with-header-container/index.tsx';
import { DonutChart } from '../../../common/ui/donut-chart/index.tsx';
import { useSummaryFeatures } from '../../../controllers/features-context/index.tsx';
import { escapeFilterChars } from '../../utils.tsx';
import { BoundaryIds, SectionIds } from '../../../common/types.tsx';
import { useUFOCustomHoverData } from '../../../common/ui/ufo-custom-data-toggle/index.tsx';
import messages from './messages.tsx';

type CategoryKey = 'new' | 'indeterminate' | 'done';

type StatusOverviewProps = {
	data: {
		issuesByStatus: {
			status: {
				name: string;
				category: { key: CategoryKey };
				jqlTerm?: string;
			};
			count: number;
		}[];
	};
};

const CustomDonutLabel = element(
	() => {
		const { formatMessage } = useIntl();
		const { activeSeriesKey } = useActiveSeriesKey();
		const metricLabel = activeSeriesKey ?? formatMessage(messages.chartLabel);

		return <Donut.elements.MetricLabel label={metricLabel} />;
	},
	{ as: Donut.elements.MetricLabel },
);

const CustomDonutValue = element(
	() => {
		const { activeSeriesKey } = useActiveSeriesKey();
		const { valueAccessor, data } = useElementProps(Donut.elements.Metric);

		const { totalValue } = useElementProps(Donut.elements.MetricValue);

		const activeSeriesValue = data.filter((datum: Datum) => datum?.label === activeSeriesKey)[0];

		// this accounts for the fact that if the total value is 0, the percentage will be 0 and not return NaN
		const value =
			activeSeriesKey && totalValue
				? Math.round((valueAccessor(activeSeriesValue) / totalValue) * 100)
				: totalValue;
		return (
			<Donut.elements.MetricValue
				totalValue={value}
				formatter={(d) => (activeSeriesKey ? `${d}%` : d)}
			/>
		);
	},
	{ as: Donut.elements.MetricValue },
);

const CustomMark = element(
	({ onHover }: { onHover: () => void }) => {
		const { onPointerMove } = useElementProps(Donut.elements.Mark);

		return (
			<Donut.elements.Mark
				onPointerMove={(e) => {
					onHover();
					onPointerMove && onPointerMove(e);
				}}
			/>
		);
	},
	{ as: Donut.elements.Mark },
);

const generateUrlOptions = (key: CategoryKey, label: string) => {
	return {
		filter: `status = '${escapeFilterChars(label)}'${key === 'done' ? ' AND statusCategoryChangedDate >= -2w' : ''}`,
	};
};

type Datum = {
	label: string;
	value: number;
	key: CategoryKey;
	jqlTerm: string;
};

const CustomDonutLegend = element(
	({ order, onHover }: { order: string[]; onHover: () => void }) => {
		const containerRef = useRef<HTMLUListElement | null>(null);
		const { data, colorAccessor, colorScale } = useElementProps(Donut.elements.Legend);
		const { linksResolver, linksTarget } = useSummaryFeatures();

		// `data` is typed as unknown in the viz library
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const typedData = data as Datum[];

		const { fireSectionAnalytics } = useSectionAnalytics(
			SectionIds.StatusOverview,
			BoundaryIds.StatusOverview,
		);

		// CSS cannot be used to determine if the container is overflowing so we need to use JS
		// to determine this and center the content if it is not overflowing and vice versa
		useEffect(() => {
			const container = containerRef.current;
			if (!container) {
				return;
			}

			if (container.scrollHeight > container.clientHeight) {
				container.style.justifyContent = 'flex-start';
			} else {
				container.style.justifyContent = 'center';
			}
		}, [containerRef]);

		const { emitActiveSeriesKey } = useActiveSeriesKeyEmitter();

		const onLegendItemClick = () => {
			fireSectionAnalytics('viewWithFilter');
		};

		return (
			<ul ref={containerRef} css={legendsContainerStyles}>
				{typedData
					.sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label))
					.map((d) => {
						const symbolSize = 12;
						const color = colorScale(colorAccessor(d));

						return (
							<li
								key={d.label}
								css={singleLegendContainerStyles}
								onPointerEnter={(e) => {
									// emitActiveSeriesKey is typed in viz library to take in HTMLDivElement as the type of the event target
									// but the event target is actually a HTMLLIElement in this case and works fine so we need to cast it
									// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
									emitActiveSeriesKey(e as any, d.label);
									onHover();
								}}
								// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
								onPointerLeave={(e) => emitActiveSeriesKey(e as any, undefined)}
							>
								<Link
									href={linksResolver(generateUrlOptions(d.key, d.jqlTerm))}
									target={linksTarget}
									css={legendLinkStyles}
									onClick={onLegendItemClick}
								>
									<div css={symbolStyles}>
										<svg width={symbolSize} height={symbolSize}>
											<rect fill={color} width={symbolSize} height={symbolSize} rx={1} ry={1} />
										</svg>
									</div>
									<span css={labelStyles}>
										{/* the first part of the string below comes from backend which is localised */}
										{/* eslint-disable-next-line jira/i18n/text-wrapped-in-format-message */}
										{d.label} ({d.value})
									</span>
								</Link>
							</li>
						);
					})}
			</ul>
		);
	},
	{ as: Donut.elements.Legend },
);

const legendsContainerStyles = css({
	overflowY: 'auto',
	flexBasis: '40%',
	display: 'flex',
	flexDirection: 'column',
	paddingTop: token('space.050', '4px'),
	paddingRight: token('space.050', '4px'),
	paddingBottom: token('space.050', '4px'),
	paddingLeft: token('space.050', '4px'),
});

const singleLegendContainerStyles = css({
	display: 'flex',
	marginTop: 0,
});

const legendLinkStyles = css({
	display: 'flex',
	gap: token('space.100', '8px'),
	'&:hover': {
		color: token('color.text.subtle'),
		textDecoration: 'none',
	},
});

const symbolStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const labelStyles = css({
	color: token('color.text.subtle'),
	font: token('font.body.UNSAFE_small'),
	margin: '8px 0px',

	'&:hover': {
		color: token('color.text.subtle'),
		textDecoration: 'none',
	},
});

export const StatusOverview = ({ data: { issuesByStatus } }: StatusOverviewProps) => {
	const { push } = useRouterActions();
	const { linksResolver, linksTarget } = useSummaryFeatures();
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.StatusOverview,
		BoundaryIds.StatusOverview,
	);
	const { addUFOCustomHoverData, UFOConditionalCustomHoverData } = useUFOCustomHoverData();

	const onFireSectionAnalytics = useCallback(
		(target: LinkTarget) => {
			fireSectionAnalytics(target);
		},
		[fireSectionAnalytics],
	);

	const onTooltipClick = useCallback(
		(params: { event: PointerEvent | FocusEvent; datum?: Datum } | undefined) => {
			if (!params) return;

			const { event, datum } = params;
			// this only gets emitted on a PointerEvent
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const buttonNumber = (event as PointerEvent).button;
			if (buttonNumber === 2 || !datum) {
				// ignore right clicks and datum should always be there but this is for type safety
				return;
			}

			onFireSectionAnalytics('viewWithFilter');
			const onClickHref = linksResolver(generateUrlOptions(datum.key, datum.jqlTerm));
			if (buttonNumber === 1) {
				// middle button should open in new tab
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(onClickHref, '_blank');
			} else if (linksTarget === '_self') {
				push(onClickHref);
			} else {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(onClickHref, linksTarget);
			}
		},
		[push, linksResolver, onFireSectionAnalytics, linksTarget],
	);

	const issuesByStatusTotal = useMemo(
		() => issuesByStatus.reduce((acc, item) => acc + item.count, 0),
		[issuesByStatus],
	);

	return (
		<>
			<ChartWithHeaderContainer
				header={
					<>
						<Heading size="small" as="h2">
							<FormattedMessage {...messages.title} />
						</Heading>
						<Text>
							{issuesByStatusTotal ? (
								<FormattedMessage
									{...messages.description}
									values={{
										link: (chunks) => (
											<Link
												href={linksResolver({
													sortBy: 'status',
													filter: BOARD_ISSUES_JQL,
													hideDone: false,
												})}
												target={linksTarget}
												onClick={() => onFireSectionAnalytics('view')}
											>
												{chunks}
											</Link>
										),
									}}
								/>
							) : (
								<FormattedMessage
									{...messages.emptyDescription}
									values={{
										link: (chunks) => (
											<Link
												href={linksResolver({
													openCreateIssueDialog: true,
												})}
												target={linksTarget}
												onClick={() => onFireSectionAnalytics('view')}
											>
												{chunks}
											</Link>
										),
									}}
								/>
							)}
						</Text>
					</>
				}
				chart={
					<DonutChart
						data={issuesByStatus.map((item) => ({
							label: item.status.name,
							value: item.count,
							key: item.status.category.key,
							jqlTerm: item.status.jqlTerm || '',
						}))}
					>
						<CustomDonutLabel />
						<CustomDonutValue />
						<CustomDonutLegend
							order={issuesByStatus.map(({ status: { name } }) => name)}
							onHover={addUFOCustomHoverData}
						/>
						<CustomMark onHover={addUFOCustomHoverData} />
						<Donut.elements.TooltipOverlay onClick={onTooltipClick} />
					</DonutChart>
				}
			/>
			<UFOConditionalCustomHoverData />
		</>
	);
};
